import { useEffect, useRef, type ReactNode } from 'react'
import './DeskGrid.css'

const FIXED_TIER_QUERY = '(min-width: 1280px) and (min-height: 720px)'

interface DeskGridProps {
  children: ReactNode
  className?: string
}

/**
 * Page-level grid for the desktop Fleet page: a fixed 12-column no-scroll
 * grid in the fixed tier, a 2-column scrolling flow grid below it. Children
 * set `--span` inline (via Module); see DeskGrid.css.
 */
export function DeskGrid({ children, className }: DeskGridProps) {
  const gridRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!import.meta.env.DEV) return
    const grid = gridRef.current
    if (!grid) return

    // In the fixed tier the document cannot overflow (grid and module faces
    // are overflow: hidden), so measure the grid and each direct child.
    const check = () => {
      if (!window.matchMedia(FIXED_TIER_QUERY).matches) return
      if (grid.scrollHeight > grid.clientHeight) {
        console.warn('desk-grid overflow', {
          element: 'grid',
          scrollHeight: grid.scrollHeight,
          clientHeight: grid.clientHeight,
        })
      }
      Array.from(grid.children).forEach((child, index) => {
        if (child.scrollHeight > child.clientHeight) {
          console.warn('desk-grid overflow', {
            element: child.getAttribute('data-drawer-key') ?? String(index),
            scrollHeight: child.scrollHeight,
            clientHeight: child.clientHeight,
          })
        }
      })
    }

    check()
    window.addEventListener('resize', check)
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null
    observer?.observe(grid)
    return () => {
      window.removeEventListener('resize', check)
      observer?.disconnect()
    }
  }, [])

  return (
    <div
      ref={gridRef}
      className={className ? `desk-grid ${className}` : 'desk-grid'}
    >
      {children}
    </div>
  )
}
